import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A square stone watch-tower. A static building whose variants are its four
// build stages — no animation. It complements the windmill by showing the
// "stages as variants" pattern on its own, and a multi-tile-ish footprint.
const STONE = "#6f6a62";
const DARK = "#4d4944";
const WOOD = "#6b4a26";

function slab(w: number, t = 0.16): Part {
  return { geometry: new THREE.BoxGeometry(w, t, w), color: DARK, position: [0, t / 2, 0] };
}

function shaft(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.62, h, 0.62), color: STONE, position: [0, 0.16 + h / 2, 0] };
}

// A ring of crenellations around the top of a shaft `h` tall.
function battlements(h: number): Part[] {
  const top = 0.16 + h;
  const out: Part[] = [];
  const n = 4;
  for (let s = 0; s < 4; s++) {
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n - 0.5;
      const along = t * 0.62;
      const edge = 0.31;
      const [x, z] = s === 0 ? [along, edge] : s === 1 ? [along, -edge] : s === 2 ? [edge, along] : [-edge, along];
      out.push({ geometry: new THREE.BoxGeometry(0.1, 0.12, 0.1), color: STONE, position: [x, top + 0.06, z] });
    }
  }
  return out;
}

export const watchTower = defineModel({
  id: "watch-tower",
  batchId: "standard",
  name: "Watch tower",
  tags: ["building", "tower", "defense", "medieval", "military"],
  summary: "A square crenellated stone tower, built across four stages.",
  author: "age-of-mcp",
  footprint: 0.45,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => [slab(0.78)] },
    { id: "stage-1", label: "Lower", parts: () => [slab(0.78), shaft(0.5)] },
    { id: "stage-2", label: "Upper", parts: () => [slab(0.78), shaft(1.0)] },
    {
      id: "complete", label: "Complete", description: "Battlements and a doorway crown the finished tower.",
      parts: () => [
        slab(0.78),
        shaft(1.3),
        ...battlements(1.3),
        { geometry: new THREE.BoxGeometry(0.2, 0.34, 0.06), color: WOOD, position: [0, 0.16 + 0.17, 0.31] },
        { geometry: new THREE.BoxGeometry(0.12, 0.16, 0.04), color: DARK, position: [0, 0.9, 0.32] },
      ],
    },
  ],
  animations: [],
});
